import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Who worked when.
 *
 * The API answers one row per employee per day, each carrying the individual
 * clock-in/clock-out entries that make up the day. The page shows that three
 * ways — per day, per person, or per entry — and all three are derived from
 * the one response rather than fetched separately.
 *
 * **Times.** These timestamps are the store's wall clock, and the original
 * shifted every one of them by `REACT_APP_TIMEZONE_OFFSET` on the way in and
 * out. That is kept: a shift that started at 8am should read 8am to the
 * manager looking at it, wherever they happen to be sitting.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

// Re-exported so a caller reaches for one module, not two, when it needs
// both the query and the clock the query is expressed in.
export {
  dayBounds,
  formatStoreTime,
  fromInputValue,
  toInputValue,
  toStoreTime,
} from '@/lib/store-time';

/** `LiteMeUserDto`, as far as this page needs it. */
export interface TimesheetUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
}

/** `EmployeeTimeSheetDto` — one clock-in to clock-out. */
export interface TimesheetEntry {
  id: string;
  startTime: string;
  endTime: string | null;
  hours: number | null;
  minutes: number | null;
  employeeId: string | null;
  businessId: string | null;
  comment: string | null;
  employeeTimeSheetTypeId: string | null;
  managed: boolean;
  employee: TimesheetUser | null;
  type: { id: string; nameEn: string | null; nameEs: string | null } | null;
}

/** `TimesheetEmployeeDateDto` — one person, one day. */
export interface TimesheetDay {
  date: string;
  businessId: string | null;
  businessName: string | null;
  totalHours: number;
  totalMinutes: number;
  user: TimesheetUser;
  timesheet: TimesheetEntry[] | null;
}

export interface TimesheetQuery {
  businessId: string;
  startDate: string;
  endDate: string;
  employeeId: string;
  timeSheetTypeId: string;
}

export function useTimesheets(query: TimesheetQuery | null) {
  return useQuery({
    queryKey: ['timesheets', query],
    queryFn: () => {
      const params = new URLSearchParams({
        businessId: query!.businessId,
        startDate: query!.startDate,
        endDate: query!.endDate,
      });
      if (query!.employeeId) params.set('employeeId', query!.employeeId);
      if (query!.timeSheetTypeId)
        params.set('timeSheetTypeId', query!.timeSheetTypeId);
      return get<{ results: TimesheetDay[] }>(`timesheet?${params}`);
    },
    select: (data) => data.results ?? [],
    enabled: !!query,
  });
}

/** Who is on the clock right now. */
export function useClockedIn(businessId: string | null) {
  return useQuery({
    queryKey: ['timesheets', 'clocked-in', businessId],
    queryFn: () =>
      get<{ results: TimesheetEntry[] }>(
        `timesheet/clockedin?businessId=${businessId}`
      ),
    select: (data) => data.results ?? [],
    enabled: !!businessId,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['timesheets'] });
}

/** `CreateTimeSheetBindingModel`. */
export interface CreateEntryInput {
  employeeId: string;
  businessId: string;
  startTime: string;
  endTime: string | null;
  employeeTimeSheetTypeId: string | null;
  comment: string | null;
}

export function useCreateTimesheet() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: CreateEntryInput) =>
      unwrap<unknown>(api.post('timesheet', input)),
    onSuccess: invalidate,
  });
}

/** `UpdateTimeSheetBindingModel`. Keyed by `timeSheetId` in the body. */
export interface UpdateEntryInput {
  timeSheetId: string;
  startTime: string;
  endTime: string | null;
  employeeTimeSheetTypeId: string | null;
  comment: string | null;
}

export function useUpdateTimesheet() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: UpdateEntryInput) =>
      unwrap<unknown>(api.put('timesheet', input)),
    onSuccess: invalidate,
  });
}

export function useDeleteTimesheet() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`timesheet/remove/${id}`)),
    onSuccess: invalidate,
  });
}

// ----------------------------------------------------------------- payroll

/**
 * What one row is worth.
 *
 * The original computed this three different ways on the same screen: the
 * gross column ignored lunch, the deduction and net columns subtracted it
 * before multiplying, and the totals row subtracted lunch *hours* from a
 * *money* figure. This is the one reading that makes sense — unpaid lunch
 * comes off the hours, and the deduction is a percentage of what is left.
 */
export function payrollFor(
  hours: number,
  lunchHours: number,
  hourlyRate: number,
  deductionPercent: number
) {
  const worked = Math.max(0, hours - lunchHours);
  const gross = worked * hourlyRate;
  const deduction = gross * (deductionPercent / 100);
  return { worked, gross, deduction, net: gross - deduction };
}
